import { useCallback } from 'react';

import { OptionInputs, useProductFormStore } from '../store/productFormStore';
import { ProductOption } from '../types/productTypes';

export const useProductOptions = () => {
  const {
    formData,
    selectedOptionPrices,
    optionInputs,
    isSaving,
    isAddMode,
    recentlyAddedOptions,
    selectOption,
    deleteOption,
    updateOptionInputs,
    updatePriceTable,
    applyPrice,
    saveOption,
    formatPrice,
    setAddMode,
    editOption,
    copyOption,
    clearRecentlyAdded,
    setDefaultOption,
  } = useProductFormStore();

  const { productOptionList, selectedOption } = formData.optionsInfo;

  const handleOptionSelect = useCallback(
    (option: ProductOption) => {
      selectOption(option);
    },
    [selectOption],
  );

  const handleOptionDelete = useCallback(
    (optionUuid: string) => {
      if (window.confirm('Are you sure you want to delete this option?')) {
        deleteOption(optionUuid);
      }
    },
    [deleteOption],
  );

  const handleOptionInputChange = useCallback(
    (field: keyof OptionInputs, value: string) => {
      updateOptionInputs(field, value);
    },
    [updateOptionInputs],
  );

  const handlePriceTableChange = useCallback(
    (
      nationCode: 'KR' | 'US',
      field: 'supplyPrice' | 'profitPrice' | 'comparePrice',
      value: string,
    ) => {
      updatePriceTable(nationCode, field, value);
    },
    [updatePriceTable],
  );

  const handlePriceApply = useCallback(() => {
    applyPrice();
  }, [applyPrice]);

  const handleOptionSave = useCallback(async () => {
    await saveOption();
  }, [saveOption]);

  const handleAddNewOption = useCallback(() => {
    setAddMode();
  }, [setAddMode]);

  const handleEditOption = useCallback(
    (option: ProductOption) => {
      editOption(option);
    },
    [editOption],
  );

  const handleCopyOption = useCallback(
    (optionUuid: string) => {
      copyOption(optionUuid);
    },
    [copyOption],
  );

  const handleClearRecentlyAdded = useCallback(
    (optionUuid: string) => {
      clearRecentlyAdded(optionUuid);
    },
    [clearRecentlyAdded],
  );

  return {
    productOptionList,
    selectedOption,
    selectedOptionPrices,
    optionInputs,
    isSaving,
    isAddMode,
    recentlyAddedOptions,
    handleOptionSelect,
    handleOptionDelete,
    handleOptionInputChange,
    handlePriceTableChange,
    handlePriceApply,
    handleOptionSave,
    handleAddNewOption,
    handleEditOption,
    handleCopyOption,
    handleClearRecentlyAdded,
    formatPrice,
  };
};
